import type {
  ICiCNormalizer,
  NormalizedCiCGroup,
  NormalizedCiCItem,
} from '@dealerportal/utilities';

/**
 * Adapts the browser CiC SDK types (V2.IItemVariant / IItemGroup) to the shared
 * NormalizedCiCItem / NormalizedCiCGroup shapes consumed by the shared
 * productBuilder utilities.
 *
 * The V2 SDK item shape is structurally very close to NormalizedCiCItem:
 * - dimensions.width/height/depth are already flat numbers
 * - dimensions.others already has { value } entries
 * - descriptions.others is already Record<string, string>
 *
 * Groups: uses group.id as the canonical id (frontend item.groupRefs contain IDs).
 */
export class CiCFrontendAdapter
  implements ICiCNormalizer<V2.IItemVariant, IItemGroup>
{
  adaptItem(raw: V2.IItemVariant): NormalizedCiCItem {
    return {
      id: raw.id,
      code: raw.code,
      names: {
        main: raw.names.main,
        others: raw.names.others as Record<string, string> | undefined,
      },
      refCodes: raw.refCodes as NormalizedCiCItem['refCodes'],
      images: raw.images as NormalizedCiCItem['images'],
      groupRefs: raw.groupRefs as string[] | undefined,
      classification: raw.classification as NormalizedCiCItem['classification'],
      descriptions: {
        main: raw.descriptions?.main,
        others: raw.descriptions?.others as Record<string, string> | undefined,
      },
      dimensions: raw.dimensions
        ? {
            width: raw.dimensions?.width,
            height: raw.dimensions?.height,
            depth: raw.dimensions?.depth,
            others: raw.dimensions?.others as
              | Record<string, { value?: number | string }>
              | undefined,
          }
        : undefined,
      context: raw.context
        ? { merchandising: raw.context.merchandising as string[] | undefined }
        : undefined,
      moreInfoLinks: raw.moreInfoLinks?.map((l) => ({
        type: l.type as string,
        uri: l.uri ?? '',
      })),
    };
  }

  adaptGroups(raw: IItemGroup[]): NormalizedCiCGroup[] {
    return raw.map((g) => this.adaptSingleGroup(g));
  }

  private adaptSingleGroup(g: IItemGroup): NormalizedCiCGroup {
    return {
      // Frontend item.groupRefs match against group.id, so id is the canonical identifier.
      id: g.id,
      name: g.name,
      type: g.type as string | undefined,
      groups: g.groups?.map((sub) => this.adaptSingleGroup(sub)),
    };
  }
}
